import { computed, inject } from '@angular/core'
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals'
import { rxMethod } from '@ngrx/signals/rxjs-interop'
import { HttpResponse, HttpStatusCode } from '@angular/common/http'
import { of } from 'rxjs'
import { catchError, map, switchMap, tap } from 'rxjs/operators'
import { AuthApi } from '@core/authentication/auth/auth.api'
import { MCUser, UserProfile } from '@core/authentication/auth/auth.models'
import { AuthFeature, AuthPage, RoleType } from '@core/authentication/auth/auth.enums'
import {
  mapMCUser,
  mapRoleFeatures,
  mapRolePermissions,
} from '@core/authentication/auth/auth.utils'
import { RouterStore } from '@core/router/router.store'
import { OidcStore } from '@core/store/oidc/oidc.store'
import { TenantStore } from '@core/store/tenant/tenant.store'
import { User } from 'oidc-client-ts'
import { environment } from '@env'

const initialState = {
  oidc: null as User | null,
  user: null as MCUser | null,
  profile: null as UserProfile | null,
}

export const ProfileStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(({ oidc, user, profile }) => {
    const roles = computed(() => profile()?.roles)
    return {
      isLoggedIn: () => !!oidc(),
      oidcFormattedToken: () => oidc() && `${oidc().token_type} ${oidc().access_token}`,
      oidcTokenId: () => oidc() && oidc()?.id_token,
      formattedProfile: () => ({
        ...profile(),
        fullName: `${profile()?.name} ${profile()?.surname}`,
      }),
      profileLang: () => profile()?.language,
      roles,
      permissions: () => mapRolePermissions(roles() || []),
      isAdmin: () => (roles() || []).includes(RoleType.Admin),
      canRefreshFeeds: () => {
        const currentRoles = roles() || []
        return currentRoles.includes(RoleType.Admin) || currentRoles.includes(RoleType.DataOperator)
      },
      userId: () => user()?.id,
      userProfiles: () => user()?.profiles,
      profileTenantConfiguration: () => profile()?.tenantConfiguration,
      profileSupportedLanguages: () => profile()?.tenantConfiguration?.SupportedLanguages,
      profileSupportedLanguagesAsDropdown: () =>
        (profile()?.tenantConfiguration?.SupportedLanguages || []).map((lang) => ({
          label: lang.translationKey,
          value: lang.id,
        })),
    }
  }),
  withMethods((store) => {
    const api = inject(AuthApi)
    const tenantStore = inject(TenantStore)
    const oidcStore = inject(OidcStore)
    const routerStore = inject(RouterStore)

    return {
      setOidcUser(oidc: User): void {
        patchState(store, { oidc })
      },
      setUserProfile(profile: UserProfile | undefined): void {
        patchState(store, { profile })
      },
      setMCUser(user: MCUser): void {
        patchState(store, { user })
      },
      purgeUserProfile(): void {
        patchState(store, { ...initialState })
      },
      fetchUserProfile: rxMethod<void>((source$) =>
        source$.pipe(
          switchMap(() =>
            api.fetchProfile().pipe(
              tap((res: HttpResponse<UserProfile>) => {
                if (res.status === HttpStatusCode.NoContent) {
                  oidcStore.logOut()
                } else {
                  patchState(store, { profile: res.body as UserProfile })
                }
              }),
              catchError((err) => {
                // TODO: Should be handled by a custom error handler (as in the old effect)
                if (err.status !== HttpStatusCode.Unauthorized) {
                  routerStore.navigateExternal(environment.IDENTITY_SERVER.POST_LOGOUT_REDIRECT_URI)
                }
                patchState(store, { profile: undefined })
                console.error('Unable to fetch user profile', err)
                return of(undefined)
              }),
            ),
          ),
        ),
      ),
      fetchMCUSer: rxMethod<void>((source$) =>
        source$.pipe(
          switchMap(() =>
            api.fetchMCUser().pipe(
              tap((users) => {
                const user = mapMCUser(users[0])
                patchState(store, { user })
                tenantStore.setSelectedTenant(user?.customer?.calculatedTenantId ?? null)
              }),
              catchError((err) => {
                console.error('Unable to fetch MC user', err)
                return of(undefined)
              }),
            ),
          ),
        ),
      ),
      saveUserProfile({ profile }: { profile: UserProfile }) {
        const currentProfile = store.profile()
        return api.updateUserProfile(profile).pipe(
          map((profiles: UserProfile[]) => ({
            ...currentProfile,
            ...profiles[0],
            roles: currentProfile?.roles,
          })),
          tap((profile: UserProfile) => patchState(store, { profile })),
        )
      },
      isFeatureAllowed(feature: AuthFeature) {
        return store.roles() ? mapRoleFeatures(store.roles()).includes(feature) : undefined
      },
      isPageAllowed(page: string) {
        return store.roles()
          ? mapRolePermissions(store.roles()).includes(page.replace(/^\//, '') as AuthPage)
          : undefined
      },
      allAvailableLanguages(isProfileView: boolean) {
        return isProfileView
          ? store.profileSupportedLanguagesAsDropdown()
          : tenantStore.tenantSupportedLanguagesAsDropdown()
      },
    }
  }),
)
